import { useMemo } from 'react'
import { useHelpdeskStore } from '@/store/helpdesk'
import type { MetaData, PaginationLink } from '@/components/tables' // for pagination


function buildClientMeta(total: number, perPage: number, currentPage: number): MetaData {
  const lastPage = Math.max(1, Math.ceil(total / perPage))

  const links: PaginationLink[] = []

  // Prev
  links.push({
    url: currentPage > 1 ? String(currentPage - 1) : null,
    label: 'prev',
    active: false,
  })

  // Pages (simple version)
  for (let p = 1; p <= lastPage; p++) {
    links.push({
      url: String(p),
      label: String(p),
      active: p === currentPage,
    })
  }

  // Next
  links.push({
    url: currentPage < lastPage ? String(currentPage + 1) : null,
    label: 'next',
    active: false,
  })

  return {
    total,
    per_page: perPage,
    current_page: currentPage,
    links,
  }
}

export function useHelpdeskRequests() {
  const items = useHelpdeskStore((s) => s.items)
  const acknowledge = useHelpdeskStore((s) => s.acknowledge)
  const setItems = useHelpdeskStore((s) => s.setItems)

  // pagination state from store
  const urgentNewPage = useHelpdeskStore((s) => s.urgentNewPage)
  const urgentNewPerPage = useHelpdeskStore((s) => s.urgentNewPerPage)
  const setUrgentNewPage = useHelpdeskStore((s) => s.setUrgentNewPage)

  const acknowledgedPage = useHelpdeskStore((s) => s.acknowledgedPage)
  const acknowledgedPerPage = useHelpdeskStore((s) => s.acknowledgedPerPage)
  const setAcknowledgedPage = useHelpdeskStore((s) => s.setAcknowledgedPage)

  // full lists
  const urgentNewAll = useMemo(() => items.filter((x) => x.status === 'NEW'), [items])
  const acknowledgedAll = useMemo(() => items.filter((x) => x.status === 'ACKNOWLEDGED'), [items])

  // meta
  const urgentNewMeta = useMemo(
    () => buildClientMeta(urgentNewAll.length, urgentNewPerPage, urgentNewPage),
    [urgentNewAll.length, urgentNewPerPage, urgentNewPage]
  )

  const acknowledgedMeta = useMemo(
    () => buildClientMeta(acknowledgedAll.length, acknowledgedPerPage, acknowledgedPage),
    [acknowledgedAll.length, acknowledgedPerPage, acknowledgedPage]
  )

  // paginated slices
  const urgentNew = useMemo(() => {
    const start = (urgentNewPage - 1) * urgentNewPerPage
    return urgentNewAll.slice(start, start + urgentNewPerPage)
  }, [urgentNewAll, urgentNewPage, urgentNewPerPage])

  const acknowledged = useMemo(() => {
    const start = (acknowledgedPage - 1) * acknowledgedPerPage
    return acknowledgedAll.slice(start, start + acknowledgedPerPage)
  }, [acknowledgedAll, acknowledgedPage, acknowledgedPerPage])

  // Pagination.tsx expects onClick(url). We'll pass "page number" as url string.
  const setUrgentNewPageFromUrl = (url: string | null) => {
    if (!url) return
    const page = Number(url)
    if (!Number.isNaN(page)) setUrgentNewPage(page)
  }

  const setAcknowledgedPageFromUrl = (url: string | null) => {
    if (!url) return
    const page = Number(url)
    if (!Number.isNaN(page)) setAcknowledgedPage(page)
  }

  return {
    items,
    urgentNew,
    urgentNewAllCount: urgentNewAll.length,
    urgentNewMeta,
    setUrgentNewPageFromUrl,

    acknowledged,
    acknowledgedAllCount: acknowledgedAll.length,
    acknowledgedMeta,
    setAcknowledgedPageFromUrl,

    acknowledge,
    setItems,
  }
}